import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";

import img from "../../Assets/Images/close-up-portrait-young-bearded-man-white-shirt-jacket-posing-camera-with-broad-smile-isolated-gray_171337-629.jpg";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { useNavigate } from "react-router-dom";

const ClientDash = () => {
  const COLORS = [
    "#30B0C7",
    "#5C7DFB",
    "#7063D4",
    "#8AE073",
    "#C274E1",
    "#E56B76",
    "#EAEB63",
    "#F48000",
  ];
  const id =
    localStorage?.getItem("SWA_UserId") ||
    sessionStorage?.getItem("SWA_UserId");

  const currentYear = new Date().getFullYear();
  const [yearOptions, setYearOptions] = useState([]);

  const [year1, setYear1] = useState(currentYear.toString());
  const [year2, setYear2] = useState(currentYear.toString());

  const [selectedJob, setSelectedJob] = useState(null);

  const Navigate = useNavigate();

  const [dashData, setDashData] = useState({});
  const [MonthlyCandidateProgression, setMonthlyCandidateProgression] =
    useState([]);
  const [clientProgress, setClientProgress] = useState([]);
  const [jobList, setJobList] = useState([]);

  const getDashData = async () => {
    try {
      const res = await axiosAPI?.post(`/clientportal/dasboard/${id}`);
      setDashData(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getJobs = async () => {
    const trimmedFilters = {
      JD_Title: "",
      JD_Country: "",
      JD_Qualification: "",
      JD_Profession: "",
      JD_Status: "",
    };

    try {
      const res = await axiosAPI.post(
        `/clientportal/joblist/${id}`,
        trimmedFilters
      );

      setJobList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getMonthlyCandidateStatistics = async () => {
    try {
      const res = await axiosAPI?.post(`/clientportal/Progress/${id}`, {
        year: year2,
        jobId: selectedJob || null,
      });
      setClientProgress(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getMonthlyCandidateProgression = async () => {
    try {
      const res = await axiosAPI?.post(`/clientportal/yearlyStats/${id}`, {
        year: year1,
      });
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const formattedData = res?.data?.data.map((item) => ({
        ...item,
        month: monthNames[item.month - 1],
      }));
      setMonthlyCandidateProgression(formattedData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const endYear = currentYear + 10;

    const options = Array.from({ length: endYear - startYear + 1 }, (_, i) => {
      const year = startYear + i;
      return { label: String(year), value: String(year) };
    });

    setYearOptions(options);

    getDashData();
    getJobs();
  }, []);

  useEffect(() => {
    getMonthlyCandidateStatistics();
  }, [year2, selectedJob]);

  useEffect(() => {
    getMonthlyCandidateProgression();
  }, [year1]);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mt: 3,
          textAlign: "left",
          fontFamily: "Geist, sans-serif",
          color: "#333",
          fontWeight: "600",
        }}
      >
        Client Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {[
          {
            icon: (
              <WorkIcon
                sx={{
                  color: "#rgb(236, 233, 233)",
                  "&:hover": { color: "white" },
                }}
              />
            ),
            title: "Jobs",
            value: dashData?.totalJobsPosted,
            link: "/Jobs",
          },
          {
            icon: (
              <PersonIcon
                sx={{
                  color: "#rgb(236, 233, 233)",
                  "&:hover": { color: "white" },
                }}
              />
            ),
            title: "Candidates",
            value: dashData?.totalLinkedCandidates,
            link: "/Candidates",
          },
          {
            icon: (
              <HowToRegIcon
                sx={{
                  color: "#rgb(236, 233, 233)",
                  "&:hover": { color: "white" },
                }}
              />
            ),
            title: "Candidates Deployed",
            value: dashData?.totalDeployed,
            link: "/DeployedCandidates",
          },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} md={6} lg={4} xl={4} key={index}>
            <Card sx={{ boxShadow: "none" }}>
              <CardContent
                sx={{
                  textAlign: "center",
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  border: "2px solid #006CB8",
                  borderRadius: "10px",
                }}
              >
                <Avatar
                  sx={{
                    background: "#006CB8",
                    border: "2px solid rgb(236, 233, 233)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.2)",
                    },
                  }}
                  onClick={() => {
                    Navigate(item?.link);
                  }}
                >
                  {item?.icon}
                </Avatar>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    padding: "10px",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "Geist, sans-serif",
                      letterSpacing: "1px",
                      fontWeight: "600",
                    }}
                  >
                    {item.value} +
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Geist, sans-serif",
                      letterSpacing: "1px",
                      fontWeight: "400",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              height: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2, // spacing between wrapped items (responsive)
              }}
            >
              {/* Title */}
              <Box sx={{ flexGrow: 1, minWidth: "200px" }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Candidate Statistics
                </Typography>
              </Box>

              {/* Single Select Field */}
              <Box
                sx={{
                  minWidth: "200px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={yearOptions}
                  value={year1}
                  onChange={(e) => setYear1(e.target.value)}
                />
              </Box>
            </Box>

            <ResponsiveContainer
              width="100%"
              height="100%"
              style={{
                padding: "40px 0px",
                position: "relative",
                right: "20px",
              }}
            >
              {MonthlyCandidateProgression.some(
                (item) => item.totalCandidates !== 0 || item.totalJoined !== 0
              ) ? null : (
                <Box
                  height={300}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ color: "#999", fontSize: "18px", position: "fixed" }}
                >
                  No data to show
                </Box>
              )}

              <BarChart data={MonthlyCandidateProgression}>
                <XAxis dataKey="month" stroke="#333" />
                <YAxis stroke="#333" />

                <Legend />
                {MonthlyCandidateProgression.some(
                  (item) => item.totalCandidates !== 0 || item.totalJoined !== 0
                ) && <Tooltip />}
                <Bar dataKey="totalJoined" fill="#3F51B5" name="Deployed" />
                <Bar dataKey="totalCandidates" fill="#9B59B6" name="Applied" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              minHeight: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2, // general spacing between wrapped items
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: "200px" }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Candidate Progression
                </Typography>
              </Box>

              {/* Filters */}
              <Box
                sx={{
                  display: "flex",
                  // flexWrap: "now",
                  justifyContent: "flex-end",
                  gap: 2,
                  flexGrow: 1,
                  minWidth: "250px",
                }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={yearOptions}
                  onChange={(e) => {
                    setYear2(e.target.value);
                  }}
                  value={year2}
                  // placeholder="2025"
                />
                <SelectInput
                  value={selectedJob}
                  name="jobid"
                  onChange={(e) => setSelectedJob(e?.target?.value)}
                  options={jobList?.map((item) => ({
                    value: item?.JD_Id,
                    label: item?.JD_Title,
                  }))}
                  placeholder="Select Job"
                  maxWidth="250px"
                />
              </Box>
            </Box>

            {clientProgress.every((item) => item.count === 0) ? (
              <Box
                sx={{
                  width: "100%",
                  height: 300,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: "18px",
                  color: "text.secondary",
                }}
              >
                No data to show
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                {/* Pie Chart Container */}
                <ResponsiveContainer width="50%" height={300}>
                  <PieChart>
                    <Pie
                      data={clientProgress}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      label
                    >
                      {clientProgress.map((entry, index) => (
                        <Cell
                          key={`cell-${entry?.status}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                {/* Custom Legend */}
                <Box sx={{ width: "45%", pl: 2 }}>
                  {clientProgress.map((entry, index) => (
                    <Box
                      key={entry.status}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: COLORS[index % COLORS.length],
                          borderRadius: "50%",
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2">
                        {entry.status} ({entry.count})
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ClientDash;
